import React, { useState } from "react";
import "../ForgotPassword/forgotPassword.css";
import apiClient from "../../api/apiClient";
import { useToast } from "../../context/toastContext";

const generatePassword = () => {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const special = '!@#$%^&*(),.?":{}|<>';
    const all = upper + lower + numbers + special;
    const pick = (str) => str[crypto.getRandomValues(new Uint8Array(1))[0] % str.length];
    const chars = [pick(upper), pick(lower), pick(numbers), pick(special), ...Array.from({ length: 12 }, () => pick(all))];
    for (let i = chars.length - 1; i > 0; i--) {
        const j = crypto.getRandomValues(new Uint8Array(1))[0] % (i + 1);
        [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join("");
};

const ChangePassword = ({ setShowChangePassword, email, session, onPasswordChanged }) => {
    const toast = useToast();
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [passwordRequirements, setPasswordRequirements] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
    });

    const handleGeneratePassword = () => {
        const generated = generatePassword();
        setNewPassword(generated);
        setConfirmPassword(generated);
        setShowPassword(true);
        validatePassword(generated);
    };

    const validatePassword = (password) => {
        setPasswordRequirements({
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /[0-9]/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        });
    };

    const handlePasswordChange = async (event) => {
        event.preventDefault();
        if (newPassword !== confirmPassword) {
            toast.error("Passwords do not match");
            return;
        }
        if (!Object.values(passwordRequirements).every((req) => req)) {
            toast.error("Please meet all password requirements");
            return;
        }
        setSubmitting(true);
        try {
            const response = await apiClient.post(`/user/change-password`, {
                username: email,
                password: newPassword,
                session,
            });
            onPasswordChanged(response.data);
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.error || "Failed to change password");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="forgot-password-container">
            <div className="forgot-password-form">
                <h3>Create New Password</h3>
                <form>
                    <p>Please enter your new password below.</p>
                    <input type="hidden" autoComplete="username" value={email} readOnly />
                    <input
                        type={showPassword ? "text" : "password"}
                        placeholder="New Password"
                        value={newPassword}
                        name="new-password"
                        autoComplete="new-password"
                        onChange={(e) => {
                            setNewPassword(e.target.value);
                            validatePassword(e.target.value);
                        }}
                        required
                    />
                    <button type="button" className="generate-password-btn" onClick={handleGeneratePassword}>
                        Generate Password
                    </button>
                    <div className="password-requirements">
                        <div className={`requirement${passwordRequirements.length ? " met" : ""}`}>
                            <span className="checkmark">{passwordRequirements.length ? "✓" : "○"}</span>
                            <span>At least 8 characters</span>
                        </div>
                        <div className={`requirement${passwordRequirements.uppercase ? " met" : ""}`}>
                            <span className="checkmark">{passwordRequirements.uppercase ? "✓" : "○"}</span>
                            <span>One uppercase letter</span>
                        </div>
                        <div className={`requirement${passwordRequirements.lowercase ? " met" : ""}`}>
                            <span className="checkmark">{passwordRequirements.lowercase ? "✓" : "○"}</span>
                            <span>One lowercase letter</span>
                        </div>
                        <div className={`requirement${passwordRequirements.number ? " met" : ""}`}>
                            <span className="checkmark">{passwordRequirements.number ? "✓" : "○"}</span>
                            <span>One number</span>
                        </div>
                        <div className={`requirement${passwordRequirements.special ? " met" : ""}`}>
                            <span className="checkmark">{passwordRequirements.special ? "✓" : "○"}</span>
                            <span>One special character</span>
                        </div>
                    </div>
                    <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Confirm New Password"
                        value={confirmPassword}
                        name="confirm-password"
                        autoComplete="new-password"
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                    />
                    <div className="show-password-toggle">
                        <input
                            type="checkbox"
                            id="changeShowPassword"
                            checked={showPassword}
                            onChange={() => setShowPassword(!showPassword)}
                        />
                        <label htmlFor="changeShowPassword">Show Password</label>
                    </div>
                    <button onClick={handlePasswordChange} disabled={submitting}>
                        {submitting ? "Setting Password…" : "Set New Password"}
                    </button>
                </form>
                <p className="back-to-login" onClick={() => setShowChangePassword(false)}>
                    Back to Login
                </p>
            </div>
        </div>
    );
};

export default ChangePassword;
